import React, { useCallback, useState, useRef, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Icon } from "semantic-ui-react";
import moment from 'moment';
import ExcelJS from "exceljs";
import { formatMoneyWithCurrency } from "../../../../../../utils/formatter";
import planDeCuentasData from "../../../../../planDeCuentas/planDeCuentasData.json";
import aiIcon from "../../../../../../assets/icons/ai-icon.svg";
import EntryLineCostCenters from "./EntryLineCostCenters";
import EntryHistoryModal from "./EntryHistoryModal";
import { getEntryCostCentersForDisplay } from "./entryLineCostCentersDisplay";
import "./EntryRow.scss";
import "./EntryLineCostCenters.scss";

const PASCALE_BOT_EMAIL = "bot@smartcfo.cl";

const ENABLE_DEVENGAR_ENTRY = true;

const isPascaleBotEmail = (email) =>
  String(email || "")
    .toLowerCase()
    .trim() === PASCALE_BOT_EMAIL;

/** Reversal copy created from an original entry (not the entry that was reversed). */
const isReversalCopyEntry = (entry) => {
  const desc = (entry?.description || '').trim();
  return /^Reversado de:/i.test(desc) || /^Reverso:/i.test(desc);
};

/** Original entry that was reversed (shows "Asiento fue reversado" tag). */
const isReversedOriginalEntry = (entry) =>
  entry?.reversed === true && !isReversalCopyEntry(entry);

/**
 * Helper function to get account hierarchy from account key
 * @param {string} accountKey - The account key (e.g., "1.02.01.01")
 * @returns {string[]} - Array of parent category labels (only first 3 levels, without duplicates)
 */
const getAccountHierarchy = (accountKey) => {
  if (!accountKey) return [];
  
  const hierarchy = [];
  const parts = accountKey.split('.');
  
  // Function to recursively search for account in tree
  const findAccountInTree = (nodes, targetKey) => {
    for (const node of nodes) {
      if (node.key === targetKey) {
        return node;
      }
      if (node.children) {
        const found = findAccountInTree(node.children, targetKey);
        if (found) return found;
      }
    }
    return null;
  };
  
  // Build hierarchy for only the first 3 levels (level 1, 2, and 3)
  const maxLevels = Math.min(3, parts.length);
  for (let i = 1; i <= maxLevels; i++) {
    const currentKey = parts.slice(0, i).join('.');
    const account = findAccountInTree(planDeCuentasData, currentKey);
    if (account && account.name) {
      // Only add if it's different from the last added name
      if (hierarchy.length === 0 || hierarchy[hierarchy.length - 1] !== account.name) {
        hierarchy.push(account.name);
      }
    }
  }
  
  return hierarchy;
};

/** Numeric value for Excel (number type, not text) */
const getNumberValue = (num) => {
  if (num === null || num === undefined || num === '') return 0;
  const n = typeof num === 'string' ? parseFloat(num) : Number(num);
  return isNaN(n) ? 0 : (n % 1 === 0 ? Math.floor(n) : n);
};

const SOURCE_TRANSACTION = 'TRANSACTION';
const SOURCE_DOCUMENT = 'DOCUMENT';

/** Normalize GraphQL/Mongo-style ids to string */
const normalizeEntryRefId = (raw) => {
  if (raw == null || raw === '') return null;
  if (typeof raw === 'string' || typeof raw === 'number') return String(raw);
  if (typeof raw === 'object') {
    if (raw.id != null) return String(raw.id);
    if (raw._id != null) return normalizeEntryRefId(raw._id);
    if (raw.$oid != null) return String(raw.$oid);
  }
  return null;
};

/**
 * Resolve deep links for match/emissions: movimiento y/o documento de forma independiente.
 * Legacy transactionId / documentId solo cuando sources está vacío.
 */
const getEntryDeepLinks = (entry) => {
  if (!entry) return [];
  const sources = Array.isArray(entry.sources) ? entry.sources : [];
  const txFromSource = sources.find((s) => s?.type === SOURCE_TRANSACTION);
  const txId = normalizeEntryRefId(txFromSource?.id)
    || (sources.length === 0 ? normalizeEntryRefId(entry.transactionId) : null);

  const documentIdsOrdered = [];
  const seenDocIds = new Set();
  sources.forEach((s) => {
    if (s?.type !== SOURCE_DOCUMENT) return;
    const id = normalizeEntryRefId(s.id);
    if (!id || seenDocIds.has(id)) return;
    seenDocIds.add(id);
    documentIdsOrdered.push(id);
  });
  if (documentIdsOrdered.length === 0 && sources.length === 0) {
    const legacyDocId = normalizeEntryRefId(entry.documentId);
    if (legacyDocId) documentIdsOrdered.push(legacyDocId);
  }

  const links = [];
  if (txId) {
    const params = new URLSearchParams({
      type: 'ALL',
      page: '1',
      offset: '50',
      transactionId: txId,
      isUnique: 'true',
      isAccounting: 'true',
    });
    links.push({
      key: 'movement',
      href: `${window.location.origin}/conciliacion?${params.toString()}`,
      label: 'Ver movimiento',
    });
  }
  documentIdsOrdered.forEach((docId) => {
    const params = new URLSearchParams({
      type: 'ALL',
      page: '1',
      offset: '50',
      documentId: docId,
      id: docId,
      isAccounting: 'true',
    });
    links.push({
      key: `document-${docId}`,
      href: `${window.location.origin}/documentos_tributarios?${params.toString()}`,
      label: 'Ver documento',
    });
  });
  return links;
};

/**
 * Fecha contable para mostrar: DD-MM-YYYY (backend suele enviar stringDate ISO YYYY-MM-DD).
 */
const formatAccountingDateForDisplay = (entry) => {
  if (!entry) return '';
  const raw = entry.stringDate;
  if (raw) {
    const strict = moment(raw, ['YYYY-MM-DD', moment.ISO_8601], true);
    if (strict.isValid()) return strict.format('DD-MM-YYYY');
    const loose = moment(raw);
    if (loose.isValid()) return loose.format('DD-MM-YYYY');
    return raw;
  }
  const ed = entry.entryDate;
  if (ed?.year != null && ed?.month != null && ed?.day != null) {
    return `${String(ed.day).padStart(2, '0')}-${String(ed.month).padStart(2, '0')}-${ed.year}`;
  }
  return '';
};

/**
 * EntryRow - Reusable component for displaying a journal entry
 * @param {Object} entry - The entry object
 * @param {string} currency - Currency code (default: 'CLP')
 * @param {string} classNamePrefix - Prefix for CSS classes (default: 'entry')
 * @param {Function} onEditEntry - Optional. Called when user chooses "Editar Asiento"
 * @param {Function} onDuplicateEntry - Optional. Copiar Asiento (solo asientos libres)
 * @param {Function} onReverseEntry - Optional. Reversar Asiento debe/haber (solo asientos libres)
 * @param {Function} onDeleteEntry - Optional. Called when user chooses "Eliminar Asiento"
 */
const EntryRow = ({
  entry,
  currency = 'CLP',
  classNamePrefix = 'entry',
  costCenterCatalogById,
  onEditEntry,
  onDuplicateEntry,
  onReverseEntry,
  onDevengarEntry,
  onDeleteEntry,
  previewMode = false,
  previewTitle = 'VISTA PREVIA DE CENTRALIZACIÓN',
}) => {
  const { t } = useTranslation("accounting");
  const { selectedCompany } = useSelector((state) => state.companyList);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return;
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [menuOpen]);

  const handleExportExcel = useCallback(async () => {
    if (!entry) return;
    try {
      const workbook = new ExcelJS.Workbook();
      const sheetName = `Asiento ${entry.entryNumber}`.substring(0, 31);
      const worksheet = workbook.addWorksheet(sheetName);
      const displayCurrency = entry.currency || currency || 'CLP';
      const totalDebit = entry.entries.reduce((sum, row) => sum + (row.debit || 0), 0);
      const totalCredit = entry.entries.reduce((sum, row) => sum + (row.credit || 0), 0);

      let currentRow = 1;
      if (selectedCompany?.name) {
        worksheet.getCell(`A${currentRow}`).value = selectedCompany.name;
        worksheet.getCell(`A${currentRow}`).font = { bold: true, size: 12 };
        currentRow++;
      }
      if (selectedCompany?.rut) {
        worksheet.getCell(`A${currentRow}`).value = `RUT: ${selectedCompany.rut}`;
        currentRow++;
      }
      currentRow++;
      worksheet.getCell(`A${currentRow}`).value = entry.entryType === 'matchEntry' || entry.entryType === 'emissionEntry'
        ? `Asiento de ${entry.entryType === 'matchEntry' ? 'MATCH' : 'EMISIÓN'} #${entry.entryNumber}`
        : `Asiento libre #${entry.entryNumber}`;
      worksheet.getCell(`A${currentRow}`).font = { bold: true };
      currentRow++;
      worksheet.getCell(`A${currentRow}`).value = `Fecha contable: ${formatAccountingDateForDisplay(entry)}`;
      currentRow++;
      if (entry.description) {
        worksheet.getCell(`A${currentRow}`).value = entry.description;
        currentRow++;
      }
      currentRow++;
      worksheet.getCell(`A${currentRow}`).value = 'Cuenta';
      worksheet.getCell(`B${currentRow}`).value = 'DEBE';
      worksheet.getCell(`C${currentRow}`).value = 'HABER';
      [worksheet.getCell(`A${currentRow}`), worksheet.getCell(`B${currentRow}`), worksheet.getCell(`C${currentRow}`)].forEach((c) => { c.font = { bold: true }; });
      currentRow++;
      entry.entries.forEach((rowData) => {
        worksheet.getCell(`A${currentRow}`).value = `${rowData.accountKey || ''} ${rowData.accountName || ''}`.trim() || '-';
        worksheet.getCell(`B${currentRow}`).value = getNumberValue(rowData.debit);
        worksheet.getCell(`C${currentRow}`).value = getNumberValue(rowData.credit);
        currentRow++;
      });
      worksheet.getCell(`A${currentRow}`).value = 'Total';
      worksheet.getCell(`A${currentRow}`).font = { bold: true };
      worksheet.getCell(`B${currentRow}`).value = getNumberValue(totalDebit);
      worksheet.getCell(`C${currentRow}`).value = getNumberValue(totalCredit);
      worksheet.getCell(`B${currentRow}`).font = { bold: true };
      worksheet.getCell(`C${currentRow}`).font = { bold: true };
      worksheet.getColumn(1).width = 45;
      worksheet.getColumn(2).width = 16;
      worksheet.getColumn(3).width = 16;

      const safeDate = (formatAccountingDateForDisplay(entry) || '').replace(/\//g, '-');
      const fileName = `asiento-${entry.entryNumber}-${safeDate || 'export'}.xlsx`;
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting entry to Excel:', err);
    }
  }, [entry, currency, selectedCompany]);

  const deepLinks = useMemo(() => {
    if (!entry || (entry.entryType !== 'matchEntry' && entry.entryType !== 'emissionEntry')) return [];
    return getEntryDeepLinks(entry);
  }, [entry]);

  // Deep link to the reversal counterpart (original <-> reversed entry).
  const reverseDeepLink = useMemo(() => {
    const counterpartId = normalizeEntryRefId(entry?.reversedInformation?.reverseCounterPartId);
    if (!counterpartId) return null;
    const params = new URLSearchParams({ entryId: counterpartId });
    return {
      key: `reverse-${counterpartId}`,
      href: `${window.location.origin}/asientos?${params.toString()}`,
      label: isReversalCopyEntry(entry)
        ? t('entryRow.viewReverseOriginal')
        : t('entryRow.viewReversedEntry'),
    };
  }, [entry, t]);

  // Accrual (devengo) links. The source/original entry has sourceEntryId === its own
  // id; the generated accrual/reversal entries point back to the original.
  const ownEntryId = normalizeEntryRefId(entry?.id ?? entry?._id);
  const devengoSourceId = normalizeEntryRefId(entry?.devengadoInformation?.sourceEntryId);
  const isDevengadoOriginal = !!devengoSourceId && !!ownEntryId && devengoSourceId === ownEntryId;

  const devengoSourceDeepLink = useMemo(() => {
    if (!devengoSourceId || isDevengadoOriginal) return null;
    const params = new URLSearchParams({ entryId: devengoSourceId });
    return {
      key: `devengo-source-${devengoSourceId}`,
      href: `${window.location.origin}/asientos?${params.toString()}`,
      label: t('entryRow.viewDevengoSource'),
    };
  }, [devengoSourceId, isDevengadoOriginal, t]);

  const allDeepLinks = useMemo(
    () => [...deepLinks, ...(reverseDeepLink ? [reverseDeepLink] : []), ...(devengoSourceDeepLink ? [devengoSourceDeepLink] : [])],
    [deepLinks, reverseDeepLink, devengoSourceDeepLink],
  );

  const entryCostCenters = useMemo(() => {
    if (!entry) return [];
    return getEntryCostCentersForDisplay(
      entry,
      costCenterCatalogById,
      entry.currency || currency || 'CLP',
    );
  }, [entry, costCenterCatalogById, currency]);

  const menuOptions = useMemo(() => {
    if (!entry || previewMode) return [];
    return [
      { key: 'export', text: t('entryRow.exportExcel'), onClick: () => { setMenuOpen(false); handleExportExcel(); } },
      {
        key: 'history',
        text: t('entryHistory.menuAction'),
        onClick: () => { setMenuOpen(false); setShowHistoryModal(true); },
      },
      ...(onEditEntry
        ? [{
            key: 'edit',
            text: t('entryRow.edit'),
            disabled: isDevengadoOriginal,
            title: isDevengadoOriginal ? t('entryRow.editDisabledDevengado') : undefined,
            onClick: () => { setMenuOpen(false); onEditEntry(entry); },
          }]
        : []),
      ...(onDuplicateEntry && entry.entryType === 'freeEntry'
        ? [{ key: 'duplicate', text: t('entryRow.copy'), onClick: () => { setMenuOpen(false); onDuplicateEntry(entry); } }]
        : []),
      ...(onReverseEntry && entry.entryType === 'freeEntry'
        ? [{
            key: 'reverse',
            text: t('entryRow.reverse'),
            disabled: entry.reversed === true,
            title: entry.reversed === true ? t('entryRow.reverseDisabled') : undefined,
            onClick: () => { setMenuOpen(false); onReverseEntry(entry); },
          }]
        : []),
      ...(ENABLE_DEVENGAR_ENTRY && onDevengarEntry && (entry.entryType === 'emissionEntry' || entry.entryType === 'freeEntry')
        ? [{
            key: 'devengar',
            text: t('entryRow.devengar'),
            disabled: entry.reversed === true || isDevengadoOriginal,
            title: entry.reversed === true
              ? t('entryRow.devengarDisabled')
              : isDevengadoOriginal
                ? t('entryRow.devengarAlreadyDevengado')
                : undefined,
            onClick: () => { setMenuOpen(false); onDevengarEntry(entry); },
          }]
        : []),
      ...(onDeleteEntry ? [{ key: 'delete', text: t('entryRow.delete'), onClick: () => { setMenuOpen(false); onDeleteEntry(entry); } }] : []),
    ];
  }, [t, entry, previewMode, onEditEntry, onDuplicateEntry, onReverseEntry, onDevengarEntry, onDeleteEntry, handleExportExcel, isDevengadoOriginal]);

  const entryTitle = useMemo(() => {
    if (!entry) return '';
    if (entry.entryType === 'matchEntry') return t('entryRow.matchTitle', { number: entry.entryNumber });
    if (entry.entryType === 'emissionEntry') return t('entryRow.emissionTitle', { number: entry.entryNumber });
    return t('entryRow.freeTitle', { number: entry.entryNumber });
  }, [t, entry]);

  if (!entry) return null;

  const displayCurrency = entry.currency || currency || 'CLP';

  const totalDebit = entry.entries.reduce((sum, row) => sum + (row.debit || 0), 0);
  const totalCredit = entry.entries.reduce((sum, row) => sum + (row.credit || 0), 0);

  return (
    <>
    <div className={`${classNamePrefix}-row-container ${classNamePrefix}-card${previewMode ? ` ${classNamePrefix}-row-container--preview` : ''}`}>
      {/* Entry Header */}
      <header className={`${classNamePrefix}-header`}>
        <div className={`${classNamePrefix}-header-left`}>
          <div className={`${classNamePrefix}-number`}>
            {previewMode ? '—' : entry.entryNumber}
          </div>
          <div className={`${classNamePrefix}-title-block`}>
            <div className={`${classNamePrefix}-title-row`}>
              <div className={`${classNamePrefix}-title`}>
                {previewMode ? previewTitle : entryTitle}
              </div>
              {isDevengadoOriginal && (
                <span className={`${classNamePrefix}-devengado-tag`}>
                  {t('entryRow.devengadoTag')}
                </span>
              )}
              {isReversedOriginalEntry(entry) && (
                <span className={`${classNamePrefix}-reversado-tag`}>
                  {t('entryRow.reversadoTag')}
                </span>
              )}
              {allDeepLinks.length > 0 && (
                <span className={`${classNamePrefix}-title-deep-links`}>
                  {allDeepLinks.map((link, i) => (
                    <React.Fragment key={link.key}>
                      {i > 0 && (
                        <span className={`${classNamePrefix}-title-deep-link-sep`} aria-hidden="true">·</span>
                      )}
                      <a
                        href={link.href}
                        className={`${classNamePrefix}-title-deep-link`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => { e.stopPropagation(); }}
                      >
                        <span className="entry-title-deep-link-content">
                          <span className="entry-title-deep-link-text">{link.label}</span>
                          <Icon
                            name="arrow up right"
                            className="entry-title-deep-link-icon"
                            aria-hidden
                          />
                        </span>
                      </a>
                    </React.Fragment>
                  ))}
                </span>
              )}
            </div>
            {entry.description && (
              <div className={`${classNamePrefix}-doc`}>
                {entry.description}
              </div>
            )}
          </div>
        </div>
        <div className={`${classNamePrefix}-header-right`} ref={menuRef}>
          <div className={`${classNamePrefix}-date-stack`}>
            <div className={`${classNamePrefix}-date-label`}>{t('entryRow.accountingDate')}</div>
            <div className={`${classNamePrefix}-date-pill`}>
              <div className={`${classNamePrefix}-date-pill-icon`}></div>
              <span>{formatAccountingDateForDisplay(entry)}</span>
            </div>
          </div>
          {!previewMode && (
          <div className={`${classNamePrefix}-header-actions-wrap`}>
            <button
              type="button"
              className={`${classNamePrefix}-header-actions-trigger`}
              aria-label={t('entryRow.moreOptions')}
              aria-expanded={menuOpen}
              onClick={(e) => { e.stopPropagation(); setMenuOpen((v) => !v); }}
            >
              <span className={`${classNamePrefix}-header-actions-dots`} aria-hidden>⋯</span>
            </button>
            {menuOpen && (
              <div className={`${classNamePrefix}-header-actions-menu`} role="menu">
                {menuOptions.map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    role="menuitem"
                    className={`${classNamePrefix}-header-actions-menu-item`}
                    disabled={opt.disabled}
                    title={opt.title}
                    aria-disabled={opt.disabled ? true : undefined}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (opt.disabled) return;
                      opt.onClick();
                    }}
                  >
                    {opt.text}
                  </button>
                ))}
              </div>
            )}
          </div>
          )}
        </div>
      </header>

      {/* Entry Body */}
      <main className={`${classNamePrefix}-body`}>
        <div className={`${classNamePrefix}-section-label`}>{t('entryRow.accountsLabel')}</div>
        <div className={`${classNamePrefix}-accounts-list`} role="table" aria-label={t('entryRow.accountsLabel')}>
          <div className={`${classNamePrefix}-accounts-row ${classNamePrefix}-accounts-header`} role="row">
            <div role="columnheader">{t('entryRow.accountColumn')}</div>
            <div role="columnheader" className="entry-amount">{t('entryRow.debitColumn')}</div>
            <div role="columnheader" className="entry-amount">{t('entryRow.creditColumn')}</div>
          </div>
            {entry.entries.map((row, idx) => {
              const hierarchy = getAccountHierarchy(row.accountKey);
              const hierarchyText = hierarchy.length > 0 ? hierarchy.join(' » ') : '';

              return (
              <div className={`${classNamePrefix}-accounts-row ${classNamePrefix}-accounts-item`} role="row" key={row.ranking ?? idx}>
                <div role="cell">
                    <div
                      className={`${classNamePrefix}-account-main${
                        !row.accountKey && row.accountName ? ` ${classNamePrefix}-account-main--pending` : ''
                      }`}
                    >
                      {row.accountKey
                        ? `${row.accountKey} ${row.accountName || ''}`.trim()
                        : (row.accountName || '-')}
                    </div>
                    {hierarchyText && (
                      <div className={`${classNamePrefix}-account-meta`}>
                        {hierarchyText}
                      </div>
                    )}
                </div>
                <div role="cell" className="entry-amount">
                  {formatMoneyWithCurrency(row.debit || 0, displayCurrency)}
                </div>
                <div role="cell" className="entry-amount">
                  {formatMoneyWithCurrency(row.credit || 0, displayCurrency)}
                </div>
              </div>
              );
            })}
        </div>
        {entryCostCenters.length > 0 && (
          <div className="entry-cost-centers-block">
            <EntryLineCostCenters
              costCenters={entryCostCenters}
              displayCurrency={displayCurrency}
              classNamePrefix={classNamePrefix}
            />
          </div>
        )}
      </main>

      {/* Entry Footer */}
      <footer className={`${classNamePrefix}-footer`}>
        <span className={`${classNamePrefix}-balance-info`}>
          <span>{t('entryRow.debitFooter')} <strong>{formatMoneyWithCurrency(totalDebit, displayCurrency)}</strong></span>
          <span className="pipe">|</span>
          <span>{t('entryRow.creditFooter')} <strong>{formatMoneyWithCurrency(totalCredit, displayCurrency)}</strong></span>
          {Math.abs(totalDebit - totalCredit) < 0.01 && (
            <span className={`${classNamePrefix}-ok-badge`} aria-label={t('entryRow.balanced')}>✓</span>
          )}
        </span>
        {entry.creationInformation && (entry.creationInformation.userEmail || entry.creationInformation.creationDate) && (
          <span>
            {t('entryRow.createdOn')}{" "}
            {entry.creationInformation.creationDate
              ? moment(entry.creationInformation.creationDate).format("DD/MM/YYYY HH:mm")
              : ""}{" "}
            {t('entryRow.createdBy')}{" "}
            {isPascaleBotEmail(entry.creationInformation.userEmail) ? (
              <span className="entry-pascale-badge" title={t('entryRow.pascaleAssistant')}>
                Pascale{' '}
                <img src={aiIcon} alt="" aria-hidden />
              </span>
            ) : (
              <strong>{entry.creationInformation.userEmail}</strong>
            )}
          </span>
        )}
      </footer>
    </div>
    <EntryHistoryModal
      open={showHistoryModal}
      onClose={() => setShowHistoryModal(false)}
      entryId={entry.id || entry._id}
      entry={entry}
    />
    </>
  );
};

/**
 * EntryRowSkeleton - Loading skeleton for EntryRow component
 * @param {string} classNamePrefix - Prefix for CSS classes (default: 'entry')
 */
export const EntryRowSkeleton = ({ classNamePrefix = 'entry' }) => {
  return (
    <div className={`${classNamePrefix}-row-container ${classNamePrefix}-card`}>
      {/* Entry Header Skeleton */}
      <header className={`${classNamePrefix}-header`}>
        <div className={`${classNamePrefix}-header-left`}>
          <div className={`${classNamePrefix}-number ${classNamePrefix}-skeleton-circle`}></div>
          <div className={`${classNamePrefix}-title-block`}>
            <div className={`${classNamePrefix}-title ${classNamePrefix}-skeleton-line ${classNamePrefix}-skeleton-medium`}></div>
            <div className={`${classNamePrefix}-doc ${classNamePrefix}-skeleton-line ${classNamePrefix}-skeleton-long`}></div>
          </div>
        </div>
        <div className={`${classNamePrefix}-header-right`}>
          <div className={`${classNamePrefix}-date-stack`}>
            <div className={`${classNamePrefix}-date-label ${classNamePrefix}-skeleton-line ${classNamePrefix}-skeleton-short`}></div>
            <div className={`${classNamePrefix}-date-pill ${classNamePrefix}-skeleton-line ${classNamePrefix}-skeleton-medium`}></div>
          </div>
        </div>
      </header>

      {/* Entry Body Skeleton */}
      <main className={`${classNamePrefix}-body`}>
        <div className={`${classNamePrefix}-section-label ${classNamePrefix}-skeleton-line ${classNamePrefix}-skeleton-short`}></div>
        <div className={`${classNamePrefix}-accounts-list`} role="table" aria-label="Cuentas del asiento">
          <div className={`${classNamePrefix}-accounts-row ${classNamePrefix}-accounts-header`} role="row">
            <div role="columnheader">Cuenta</div>
            <div role="columnheader" className="entry-amount">DEBE</div>
            <div role="columnheader" className="entry-amount">HABER</div>
          </div>
          {Array.from({ length: 2 }).map((_, idx) => (
            <div key={`skeleton-${idx}`} className={`${classNamePrefix}-accounts-row ${classNamePrefix}-accounts-item ${classNamePrefix}-skeleton-row`} role="row">
              <div role="cell">
                  <div className={`${classNamePrefix}-account-main ${classNamePrefix}-skeleton-line ${classNamePrefix}-skeleton-medium`}></div>
                  <div className={`${classNamePrefix}-account-meta ${classNamePrefix}-skeleton-line ${classNamePrefix}-skeleton-long`}></div>
              </div>
              <div role="cell" className="entry-amount">
                  <div className={`${classNamePrefix}-skeleton-line ${classNamePrefix}-skeleton-short`}></div>
              </div>
              <div role="cell" className="entry-amount">
                  <div className={`${classNamePrefix}-skeleton-line ${classNamePrefix}-skeleton-short`}></div>
              </div>
            </div>
            ))}
        </div>
      </main>

      {/* Entry Footer Skeleton */}
      <footer className={`${classNamePrefix}-footer`}>
        <span className={`${classNamePrefix}-balance-info`}>
          <div className={`${classNamePrefix}-skeleton-line ${classNamePrefix}-skeleton-medium`} style={{ display: 'inline-block' }}></div>
        </span>
        <div className={`${classNamePrefix}-skeleton-line ${classNamePrefix}-skeleton-long`} style={{ display: 'inline-block' }}></div>
      </footer>
    </div>
  );
};

export default EntryRow;

